import { useState } from 'react';
import { Badge, StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { Textarea } from '../../components/ui/FormField.js';

/**
 * 372 (#1240): approval of a gift certificate or a voucher (discount), shared by both pages. The server decides who
 * may approve (`decision` on the detail read); these only show it. A record with no approval was issued before #1240
 * and counts as approved.
 */
export interface Approval {
  status: 'pending' | 'approved' | 'rejected';
  requested_by_name: string;
  requested_at: string;
  requested_for: 'created' | 'increased';
  change?: string[];
  decided_by_name?: string;
  decided_at?: string | null;
  reason?: string;
}
/** 372 (FR-005): one line per request and decision. */
export interface ApprovalHistoryEntry {
  action: 'requested' | 'approved' | 'rejected';
  by_name: string;
  at: string;
  requested_for?: 'created' | 'increased';
  change?: string[];
  reason?: string;
}
export type ApprovalFilterValue = '' | 'pending' | 'approved' | 'rejected';

/** 375: a list opened as `?approval=pending` (the promo dashboard's Review link) starts on that filter. */
export function approvalFromUrl(search: string): ApprovalFilterValue {
  const v = new URLSearchParams(search).get('approval');
  return v === 'pending' || v === 'approved' || v === 'rejected' ? v : '';
}
export type DecisionFor = 'allowed' | 'own' | 'no_permission' | null;
export type ApprovalKind = 'certificate' | 'voucher';

const when = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        timeZone: 'Asia/Kuala_Lumpur',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    : '';

export function ApprovalBadge({ approval }: { approval?: Approval | null }) {
  if (approval?.status === 'pending') return <Badge variant="warning">Pending approval</Badge>;
  if (approval?.status === 'rejected') return <Badge variant="danger">Rejected</Badge>;
  return <Badge variant="success">Approved</Badge>;
}

/** "value raised by Cik Ros" / "issued by Cik Ros". */
export function requestLine(a: Approval, kind: ApprovalKind): string {
  if (a.requested_for === 'increased')
    return `${(a.change ?? []).join(', ') || 'increase'} raised by ${a.requested_by_name || 'someone'}`;
  return `${kind === 'certificate' ? 'issued' : 'created'} by ${a.requested_by_name || 'someone'}`;
}

export function PendingBanner({ count, kind }: { count: number; kind: ApprovalKind }) {
  if (count <= 0) return null;
  const noun = kind === 'certificate' ? 'certificate' : 'voucher';
  const verb = kind === 'certificate' ? 'issue' : 'create or raise';
  return (
    <p
      data-testid="approval-banner"
      className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-2 text-sm text-yellow-900 dark:border-yellow-500/40 dark:bg-yellow-500/10 dark:text-yellow-200"
    >
      <strong>{count} waiting for approval.</strong>{' '}
      {kind === 'certificate'
        ? `A new ${noun} cannot be used until an administrator who did not ${verb} it approves it.`
        : `A new or increased ${noun} cannot be used until an administrator who did not ${verb} it approves it.`}
    </p>
  );
}

export function ApprovalChips({
  value,
  onChange,
  pendingCount,
}: {
  value: ApprovalFilterValue;
  onChange: (v: ApprovalFilterValue) => void;
  pendingCount?: number;
}) {
  const chips: Array<[ApprovalFilterValue, string]> = [
    ['', 'All'],
    ['pending', `Pending approval${pendingCount ? ` (${pendingCount})` : ''}`],
    ['approved', 'Approved'],
    ['rejected', 'Rejected'],
  ];
  return (
    <fieldset aria-label="Approval" className="mb-3 flex flex-wrap gap-2">
      {chips.map(([v, label]) => (
        <Button
          key={v || 'all'}
          variant={value === v ? 'primary' : 'secondary'}
          aria-pressed={value === v}
          onClick={() => onChange(v)}
        >
          {label}
        </Button>
      ))}
    </fieldset>
  );
}

/** "8 Oct 2026, 15:10 · value raised by Hao Kok" / "… · rejected by Hao Kok: Duplicate". */
export function historyLine(e: ApprovalHistoryEntry, kind: ApprovalKind): string {
  const who = e.by_name || 'someone';
  const what =
    e.action === 'approved'
      ? `approved by ${who}`
      : e.action === 'rejected'
        ? `rejected by ${who}${e.reason ? `: ${e.reason}` : ''}`
        : e.requested_for === 'increased'
          ? `${(e.change ?? []).join(', ') || 'increase'} raised by ${who}`
          : `${kind === 'certificate' ? 'issued' : 'created'} by ${who}`;
  return `${when(e.at)} · ${what}`;
}

const UNTIL: Record<ApprovalKind, string> = {
  certificate:
    "Until approved, this code is refused at checkout and does not count in the customer's Credit.",
  voucher:
    'Until approved, its codes are refused at checkout and a personal discount does not apply.',
};

/** The detail pages' Approval section. */
export function ApprovalPanel({
  approval,
  history,
  decision,
  kind,
  busy,
  error,
  onApprove,
  onReject,
  revoked = false,
}: {
  approval?: Approval | null;
  history?: ApprovalHistoryEntry[];
  decision: DecisionFor;
  kind: ApprovalKind;
  busy?: boolean;
  error?: string | null;
  onApprove: () => void;
  onReject: (reason: string) => void;
  /** 401 (#1366): a certificate revoked while it waited is never decided; only its page passes this. */
  revoked?: boolean;
}) {
  // A revoked request is not waiting: the panel says so instead of "pending". A decision already made stays shown.
  const revokedPending = revoked && approval?.status === 'pending';
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  return (
    <Card data-testid="approval-panel">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-ink">Approval</h2>
        {revokedPending ? <StatusBadge status="revoked" /> : <ApprovalBadge approval={approval} />}
      </div>
      {!approval && (
        <p className="text-sm text-steel">
          Approved before #1240 ({kind === 'certificate' ? 'issued' : 'created'} before approval
          existed).
        </p>
      )}
      {approval?.status === 'approved' && (
        <p className="text-sm text-steel">
          {`Approved by ${approval.decided_by_name || 'an administrator'} · ${when(approval.decided_at)}. `}
          {`${requestLine(approval, kind).replace(/^./, (c) => c.toUpperCase())}.`}
        </p>
      )}
      {approval?.status === 'rejected' && (
        <p className="text-sm text-steel">
          {`Rejected by ${approval.decided_by_name || 'an administrator'} · ${when(approval.decided_at)} — “${approval.reason ?? ''}”. It can never be used.`}
        </p>
      )}
      {revokedPending && (
        <p className="text-sm text-steel">
          Revoked before a decision; there is nothing to approve.
        </p>
      )}
      {approval?.status === 'pending' && !revokedPending && (
        <>
          <p className="text-sm text-steel">
            {`${requestLine(approval, kind).replace(/^./, (c) => c.toUpperCase())} · ${when(approval.requested_at)}. ${UNTIL[kind]}`}
          </p>
          {decision === 'own' && (
            <p className="mt-3 border-l-4 border-yellow-400 bg-yellow-50 px-3 py-2 text-sm text-ink dark:bg-yellow-500/10">
              {kind === 'certificate'
                ? 'You issued this certificate, so another administrator must approve it.'
                : 'You created or increased this voucher, so another administrator must approve it.'}
            </p>
          )}
          {decision === 'no_permission' && (
            <p className="mt-3 text-sm text-steel">Waiting for an administrator to approve it.</p>
          )}
          {decision === 'allowed' && !rejecting && (
            <div className="mt-3 flex gap-2">
              <Button disabled={busy} onClick={onApprove}>
                Approve
              </Button>
              <Button variant="secondary" disabled={busy} onClick={() => setRejecting(true)}>
                Reject…
              </Button>
            </div>
          )}
          {decision === 'allowed' && rejecting && (
            <div className="mt-3 flex flex-col gap-2">
              <label htmlFor="approval-reason" className="text-xs text-steel">
                Reason for rejecting (required)
              </label>
              <Textarea
                id="approval-reason"
                rows={2}
                maxLength={300}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <div className="flex gap-2">
                <Button
                  variant="danger"
                  disabled={busy || !reason.trim()}
                  onClick={() => onReject(reason.trim())}
                >
                  {kind === 'certificate' ? 'Reject certificate' : 'Reject voucher'}
                </Button>
                <Button variant="secondary" onClick={() => setRejecting(false)}>
                  Back
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      {error && (
        <p role="alert" className="mt-2 text-sm text-semantic-danger-text">
          {error}
        </p>
      )}
      {history && history.length > 1 && (
        <details className="mt-3 text-sm">
          <summary className="cursor-pointer text-steel">History</summary>
          <ul data-testid="approval-history" className="mt-2 flex flex-col gap-1 text-ink">
            {history.map((e) => (
              <li key={`${e.action}-${e.at}`}>{historyLine(e, kind)}</li>
            ))}
          </ul>
        </details>
      )}
    </Card>
  );
}
